// URL <-> filter state of the owner reporting pages. Pure functions (no browser, no React): the URL is the single source of truth
// (period, dates, vehicle, tab, page, sort, search) so a view can be shared or reloaded. Values are validated here; the server
// validates them again. Nothing in this module computes an amount.

import {
  PERIODS, GRANULARITIES, DEFAULT_FILTERS, firstValue, oneOf, positiveInt, toQueryObject, parseFilters, addFilterParams, normalizeFilters,
} from './reportingFilters';

// Period / vehicle / granularity are shared with the other reporting pages (see reportingFilters.js).
export { PERIODS, GRANULARITIES };
export const TABS = ['all', 'upcoming', 'pendingPayout', 'paid'];
export const SORT_COLUMNS = ['checkIn', 'confirmationCode', 'vehicle', 'grossAmount', 'netRevenue', 'status'];
export const PAGE_SIZE = 10;
export const SEARCH_MAX_LENGTH = 100;

export const DEFAULT_STATE = {
  ...DEFAULT_FILTERS,
  tab: 'all',
  page: 1,
  sortBy: 'checkIn',
  sortDir: 'desc',
  search: '',
};

// Reads the query object given to a route (query-string parse) or a URLSearchParams / query string.
export function parseEarningsQuery(input) {
  const query = toQueryObject(input);
  return {
    ...parseFilters(query),
    tab: oneOf(firstValue(query.tab), TABS, DEFAULT_STATE.tab),
    page: positiveInt(query.page) || 1,
    sortBy: oneOf(firstValue(query.sortBy), SORT_COLUMNS, DEFAULT_STATE.sortBy),
    sortDir: oneOf(firstValue(query.sortDir), ['asc', 'desc'], DEFAULT_STATE.sortDir),
    search: String(firstValue(query.search) || '').trim().slice(0, SEARCH_MAX_LENGTH),
  };
}

// Only values that differ from the defaults go in the URL (clean shareable links).
export function serializeEarningsQuery(state, { omit = [] } = {}) {
  const current = { ...DEFAULT_STATE, ...state };
  const params = addFilterParams(new URLSearchParams(), current, omit);
  const add = (key, value) => { if (!omit.includes(key)) params.set(key, String(value)); };
  if (current.tab !== DEFAULT_STATE.tab) add('tab', current.tab);
  if (Number(current.page) > 1) add('page', current.page);
  if (current.sortBy !== DEFAULT_STATE.sortBy) add('sortBy', current.sortBy);
  if (current.sortDir !== DEFAULT_STATE.sortDir) add('sortDir', current.sortDir);
  if (current.search) add('search', current.search);
  return params.toString();
}

export function buildPagePath(basePath, state) {
  const query = serializeEarningsQuery(state);
  return query ? `${basePath}?${query}` : basePath;
}

// Changing any filter goes back to page 1 (a page number only makes sense for the same filters).
export function applyPatch(state, patch) {
  const next = normalizeFilters({ ...state, ...patch });
  const keepsPage = Object.keys(patch).every((key) => key === 'page');
  if (!keepsPage) next.page = 1;
  return next;
}

// Variables of the getOwnerEarnings query for a state.
export function buildEarningsVariables(state, convertCurrency) {
  const variables = {
    period: state.period,
    tab: state.tab,
    page: state.page,
    pageSize: PAGE_SIZE,
    sortBy: state.sortBy,
    sortDir: state.sortDir,
    granularity: state.granularity,
  };
  if (state.period === 'custom') { variables.startDate = state.startDate; variables.endDate = state.endDate; }
  if (state.listId) variables.listId = state.listId;
  if (state.search) variables.search = state.search;
  if (convertCurrency) variables.convertCurrency = convertCurrency;
  return variables;
}

// "Export statement" link: same filters as the table (period, vehicle, tab, search, sort, currency), no pagination.
export const STATEMENT_PATH = '/owner/statement.csv';
export function buildStatementUrl(state, convertCurrency) {
  const params = new URLSearchParams();
  params.set('period', state.period);
  if (state.period === 'custom') { params.set('startDate', state.startDate); params.set('endDate', state.endDate); }
  if (state.listId) params.set('listId', String(state.listId));
  if (state.tab !== 'all') params.set('tab', state.tab);
  if (state.search) params.set('search', state.search);
  params.set('sortBy', state.sortBy);
  params.set('sortDir', state.sortDir);
  if (convertCurrency) params.set('convertCurrency', convertCurrency);
  return `${STATEMENT_PATH}?${params.toString()}`;
}

// Which screen to show. Pure so the screens can be tested without Apollo.
// loading: first load (no earlier data) ; error: network error or status 'failed' ; notLoggedIn: anonymous / administrator.
export function deriveEarningsView({ loading, networkError, earnings, state }) {
  if (earnings && earnings.status === 'notLoggedIn') return 'notLoggedIn';
  if (earnings && earnings.status === 'failed') return 'error';
  if (!earnings) return networkError ? 'error' : (loading ? 'loading' : 'error');
  const filtered = state.tab !== 'all' || Boolean(state.search) || Boolean(state.listId);
  if (Number(earnings.count) === 0) return filtered ? 'noResults' : 'empty';
  return 'data';
}
